/**
 * The Category Bands screen's messages.
 *
 * Split out of `reviewRequests.ts` in Phase 2B, Task 8, for one reason: that
 * file was 499 lines — already past this repo's 400-line hard limit — and the
 * composer/explorer keys had to land somewhere. Moving the composer half alone
 * left it at 437, so the largest remaining coherent unit came out too.
 *
 * The `seed.*` keys that used to sit beside these went with the seed panel on
 * 2026-09-06: seeding rosters and products across every client is now the
 * backend's `seed-review-requests` subcommand, not a button on this screen.
 *
 * NOT imported by any screen directly. `reviewRequests.ts` spreads this back
 * into the same `reviewRequests` namespace, so every key here is still
 * `reviewRequests.bands.*` and no import path anywhere changed.
 */
export const reviewRequestsBandsMessages = {
  bands: {
    breadcrumb: 'Category Bands',
    companyLabel: 'Shariyah Company',
    companiesLoadError: 'Could not load Shariyah companies. Please try again.',
    noCompanies: 'No Shariyah companies are configured yet.',
    bandsLoadError: 'Could not load category bands for this company. Please try again.',
    emptyState: 'This company has no category bands configured yet.',
    startFromStandard: 'Start from the standard bands',
    caption: 'Category bands for the selected Shariyah company',
    colBand: 'Band',
    colFrom: 'From page',
    colTo: 'To page',
    minPageAria: 'Band {code} starting page',
    maxPageAria: 'Band {code} ending page',
    andAbove: 'and above',
    valid: 'These thresholds are valid and ready to save.',
    note: 'These thresholds decide which band a document falls into, and therefore what a review costs. They apply to every new review request from the moment you save.',
    superAdminOnly: 'Only a super admin can save category bands.',
    savedMessage: 'Category bands updated.',
    status: {
      conflict:
        'These bands could not be saved because of a conflict. Please reload and try again.',
      unprocessable: 'The server rejected these thresholds. Please review the bands and try again.',
      forbidden: 'You do not have permission to edit category bands.',
      fallback: 'Could not save the category bands. Please try again.',
    },
    /** Mirror of `BandSetError::message()` in
     *  `srb_dev_backend/src/modules/review_requests/bands.rs`. The English
     *  is copied verbatim so a user never sees the same problem described
     *  two ways depending on which side caught it — note the seam this
     *  introduces: the SERVER's copy of these sentences is not translated,
     *  so under a non-English locale a client-caught refusal would be
     *  translated and a server-caught one would not. Harmless while this
     *  bundle is English-only; translate the backend's `message()` at the
     *  same time as adding an `ar` bucket here. */
    validation: {
      empty: 'At least one band is required.',
      invalidCode: '"{code}" is not a valid band code (A-E).',
      duplicate: 'Band {code} is listed more than once.',
      endsBeforeStart: 'Band {code} ends before it starts.',
      noOpenBand: 'The last band must have no upper limit, so no document is left unpriced.',
      multipleOpen: 'Only the last band may have no upper limit.',
      mustStartAtOne: 'The first band must start at page 1.',
      overlap: 'Bands {previous} and {next} overlap.',
      gap: 'There is a gap in page coverage after band {previous}.',
    },
  },
}
